/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable guard-for-in */
/* eslint-disable no-case-declarations */
/* eslint-disable no-continue */
/* eslint-disable no-restricted-syntax */
import { nothing } from 'lit/html.js';
import { directive, AsyncDirective } from 'lit/async-directive.js';
import { DirectiveResult } from 'lit/directive.js';

type EventListenerWithOptions = EventListenerOrEventListenerObject &
  Partial<AddEventListenerOptions>;

/**
 * Usage:
 *    import { html, render } from 'lit-html';
 *    import { spread } from '@open-wc/lit-helpers';
 *
 *    render(
 *      html`
 *        <div
 *          ${spread({
 *            'my-attribute': 'foo',
 *            '?my-boolean-attribute': true,
 *            '.myProperty': { foo: 'bar' },
 *            '@my-event': () => console.log('my-event fired'),
 *          })}
 *        ></div>
 *      `,
 *      document.body,
 *    );
 */
class SpreadDirective extends AsyncDirective {
  host!: EventTarget;
  element!: Element;
  prevData: { [key: string]: unknown } = {};

  render(spreadData: { [key: string]: unknown }) {
    return nothing;
  }
  update(part: any, [spreadData]: Parameters<this['render']>) {
    if (this.element !== part.element) {
      this.element = part.element;
    }
    this.host = part.options?.host || this.element;
    this.apply(spreadData);
    this.groom(spreadData);
    this.prevData = spreadData;
  }

  apply(data: { [key: string]: unknown }) {
    if (!data) return;
    const { prevData, element } = this;
    for (const key in data) {
      const value = data[key];
      if (value === prevData[key]) {
        continue;
      }
      const name = key.slice(1);
      switch (key[0]) {
        case '@': // event listener
          const prevHandler = prevData[key];
          if (prevHandler) {
            element.removeEventListener(
              name,
              this,
              value as EventListenerWithOptions
            );
          }
          element.addEventListener(
            name,
            this,
            value as EventListenerWithOptions
          );
          break;
        case '.': // property
          // @ts-ignore
          element[name] = value;
          break;
        case '?': // boolean attribute
          if (value) {
            element.setAttribute(name, '');
          } else {
            element.removeAttribute(name);
          }
          break;
        default:
          // standard attribute
          if (value != null) {
            element.setAttribute(key, String(value));
          } else {
            element.removeAttribute(key);
          }
          break;
      }
    }
  }

  groom(data: { [key: string]: unknown }) {
    const { prevData, element } = this;
    if (!prevData) return;
    for (const key in prevData) {
      if (!data || !(key in data)) {
        switch (key[0]) {
          case '@': // event listener
            const value = prevData[key];
            element.removeEventListener(
              key.slice(1),
              this,
              value as EventListenerWithOptions
            );
            break;
          case '.': // property
            // @ts-ignore
            element[key.slice(1)] = undefined;
            break;
          case '?': // boolean attribute
            element.removeAttribute(key.slice(1));
            break;
          default:
            // standard attribute
            element.removeAttribute(key);
            break;
        }
      }
    }
  }

  handleEvent(event: Event) {
    const value: Function | EventListenerObject = this.prevData[
      `@${event.type}`
    ] as Function | EventListenerObject;
    if (typeof value === 'function') {
      (value as Function).call(this.host, event);
    } else {
      (value as EventListenerObject).handleEvent(event);
    }
  }

  disconnected() {
    const { prevData, element } = this;
    for (const key in prevData) {
      if (key[0] !== '@') continue;
      // event listener
      const value = prevData[key];
      element.removeEventListener(
        key.slice(1),
        this,
        value as EventListenerWithOptions
      );
    }
  }

  reconnected() {
    const { prevData, element } = this;
    for (const key in prevData) {
      if (key[0] !== '@') continue;
      // event listener
      const value = prevData[key];
      element.addEventListener(
        key.slice(1),
        this,
        value as EventListenerWithOptions
      );
    }
  }
}

export const spread: (spreadData: {
  [key: string]: unknown;
}) => DirectiveResult<typeof SpreadDirective> = directive(SpreadDirective);
